import { useFetchRoles } from '@/features/auth/queries';
import { homeKeys } from '@/features/home/queries/query-keys';
import { fetchCoachByUid } from '@/features/home/services/get-coach';
import { useSessionSelectPlayerStore } from '@/features/player-session/stores/session-select-player-store';
import { useOidcInteractionStore, useSessionStore } from '@/stores';
import { useQueryClient } from '@tanstack/react-query';
import type { User } from 'firebase/auth';
import { getAuth, onAuthStateChanged, onIdTokenChanged } from 'firebase/auth';
import { useEffect, useEffectEvent } from 'react';

import { getFirebaseApp } from '../init.web';

const auth = () => getAuth(getFirebaseApp());

export const AuthStateEffect = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: fetchRoles } = useFetchRoles();
  const {
    resetSessionState,
    setSessionUser,
    setSessionAuthToken,
    setSessionInitialized,
    setIsAuthenticated,
    isPendingLoginSocial,
    setRoles,
    setActiveRole,
  } = useSessionStore();

  const handleAuthStateChanged = useEffectEvent(async (user: User | null) => {
    // Social sign-in fires onAuthStateChanged the instant signInWithPopup sets the Firebase
    // user — which races ahead of syncGuardianProfile (the call that creates the guardian doc
    // on the backend). Fetching roles here would then hit a backend with no guardian yet, get
    // empty roles, and resolvePostAuthTarget would default to 'coach'. Bail while the social
    // login owns the flow; it flips the flag back off when done, and the effect below
    // re-subscribes (so Firebase re-invokes this with the now-verified user + synced profile).
    // Mirror of the native auth-state-effect guard.
    if (isPendingLoginSocial) {
      return;
    }
    // Treat an unverified email as unauthenticated (mirror of the native
    // auth-state-effect). Sign-up signs the user in transiently to send the
    // verification email; without this guard web would flip isAuthenticated=true,
    // trigger withUnAuth's post-auth reset, then signOut resets to Welcome —
    // clobbering the navigate('VerifyEmailLinkSent') and bouncing the user back.
    if (!user || !user.emailVerified) {
      resetSessionState();
      useSessionSelectPlayerStore.getState().reset();
      queryClient.clear();
      return;
    }
    const token = await user.getIdToken();
    setSessionAuthToken(token);
    // OIDC/shop handoff pages (`/sso-login/:uid`, `/shop-bridge`) are pure redirect stops: they
    // only need the ID token to complete the handoff, not the full authenticated-app bootstrap.
    // Running it here fired `/guardians/me/roles` + a coach prefetch + Firestore listeners on
    // EVERY auth flip and on every redirect-hop page reload (each hop lands back on this origin
    // and re-boots the app) — spamming roles calls, permission-denied noise, and 401s during the
    // bridge's signOut→signInWithCustomToken token swap. Skip it while the OIDC gate is active.
    if (useOidcInteractionStore.getState().active) {
      setSessionUser(user);
      setIsAuthenticated(true);
      setSessionInitialized(true);
      return;
    }
    if (user.uid) {
      // Resolve roles from the API (/guardians/me/roles) and, in parallel, prefetch
      // the coach doc. Without the roles, resolvePostAuthTarget falls back to 'coach',
      // so a guardian on web was always landing on the coach dashboard. Mirror of the
      // native auth-state-effect; set roles BEFORE setIsAuthenticated so the post-auth
      // router (Splash / with-protect) reads them. prefetchQuery never rejects, so a
      // coach-read error can't break login.
      const [{ roles }] = await Promise.all([
        fetchRoles(),
        queryClient.prefetchQuery({
          queryKey: homeKeys.coach(user.uid),
          queryFn: () => fetchCoachByUid(user.uid),
        }),
      ]);
      setRoles(roles);
      // Auto-select the first role; RoleSelect handles the multi-role case.
      setActiveRole(roles?.[0] ?? null);
    }
    setSessionUser(user);
    setIsAuthenticated(!!user);
    setSessionInitialized(true);
  });

  const handleIdTokenChanged = useEffectEvent(async (user: User | null) => {
    if (isPendingLoginSocial) {
      return;
    }
    if (!user) {
      return;
    }
    const token = await user.getIdToken();
    setSessionAuthToken(token);
  });

  // Re-subscribe when the social-login flag flips: on the false→true→false cycle the
  // second render re-runs onAuthStateChanged, which Firebase immediately re-invokes with the
  // current (now email-verified, profile-synced) user — the point at which roles resolve
  // correctly. Mirror of the native effect's `isPendingLoginSocial` dependency.
  useEffect(() => {
    const unsub = onAuthStateChanged(auth(), handleAuthStateChanged);
    return unsub;
  }, [isPendingLoginSocial]);

  useEffect(() => {
    const unsub = onIdTokenChanged(auth(), handleIdTokenChanged);
    return unsub;
  }, [isPendingLoginSocial]);

  return null;
};
